<template>
  <div class="form-group">
    <label :for="name">{{ t(`labels.${props.label ?? props.name}`) }}</label>

    <!-- Autocomplete with InputGroup wrapper (if Suggestions) -->
    <div
      v-if="hasSuggestions"
      class="p-inputgroup"
      :class="[
        {
          'dirty-state-wrapper': isFieldDirty && !disabled,
          'error-state-wrapper': !!errorMessage && !disabled && (meta.touched || meta.dirty),
        },
      ]"
    >
      <PlaceholderSourceButton
        :placeholderSource="props.placeholderSource"
        :placeholder="formattedPlaceholder"
        :currentValue="internalValue"
        :isVisible="isPlaceholderDifferent"
        :disabled="disabled"
        @accept="handleAcceptPlaceholder"
      />
      <AutoComplete
        :id="name"
        ref="inputEl"
        v-model="viewValue"
        :suggestions="acSuggestions"
        :maxlength="props.maxlength"
        :placeholder="formattedPlaceholder"
        :disabled="disabled"
        :minLength="props.suggestMinChars ?? 1"
        :completeOnFocus="props.suggestCompleteOnFocus ?? false"
        :emptyMessage="t('messages.noResults')"
        :inputClass="alignClass"
        :pt="{ input: { class: alignClass } }"
        @focus="onFocus"
        @blur="onBlur"
        @complete="onACComplete"
        @item-select="onACItemSelect"
        @dblclick="onPlaceholderDblClick"
      />
    </div>

    <!-- Standard InputText (no Suggestions) -->
    <template v-else>
      <!-- InputGroup when field accepts placeholder data OR has placeholder -->
      <div
        v-if="acceptsPlaceholderData || !!props.placeholder"
        class="p-inputgroup"
        :class="[
          {
            'dirty-state-wrapper': isFieldDirty && !disabled,
            'error-state-wrapper': !!errorMessage && !disabled && (meta.touched || meta.dirty),
          },
        ]"
      >
        <PlaceholderSourceButton
          :placeholderSource="props.placeholderSource"
          :placeholder="formattedPlaceholder"
          :currentValue="internalValue"
          :isVisible="isPlaceholderDifferent"
          :disabled="disabled"
          @accept="handleAcceptPlaceholder"
        />
        <InputText
          :id="name"
          ref="inputEl"
          v-model="viewValue"
          :formatType="props.formatType"
          :maxlength="props.maxlength"
          :placeholder="formattedPlaceholder"
          :disabled="disabled"
          :class="alignClass"
          :type="type"
          @focus="onFocus"
          @blur="onBlur"
          @input="onInput"
          @dblclick="onPlaceholderDblClick"
        />
      </div>

      <!-- Plain InputText (if no InputGroup needed) -->
      <InputText
        v-else
        :id="name"
        ref="inputEl"
        v-model="viewValue"
        :formatType="props.formatType"
        :maxlength="props.maxlength"
        :placeholder="formattedPlaceholder"
        :disabled="disabled"
        :class="[
          'my-2',
          alignClass,
          {
            'dirty-state': isFieldDirty && !disabled,
            'error-state': !!errorMessage && !disabled && (meta.touched || meta.dirty),
          },
        ]"
        :type="type"
        @focus="onFocus"
        @blur="onBlur"
        @input="onInput"
        @dblclick="onPlaceholderDblClick"
      />

      <!-- Show selectable flags (only in edit mode) -->
      <div v-if="!disabled && isMultilang" class="mt-0 flex gap-2 flex-wrap">
        <Button
          v-for="lang in missingLocales"
          :key="lang"
          v-tooltip="{
            value: t('titletags.addLang', { locale: t(`enums.${lang.toUpperCase()}`) }),
            showDelay: 300,
          }"
          variant="text"
          type="button"
          class="px-2 py-1 rounded border text-m"
          @click="addLocaleFlag(lang)"
        >
          {{ localeToFlagMap[lang] }}
        </Button>
      </div>
    </template>

    <!-- Initial: {{ initialSnapshot }} IV: {{ internalValue }} -->
    <FormError v-if="showError" :message="errorMessage" />
    <!-- Check: IV: {{ initialValue }} {{ typeof initialValue }} | SNAP: {{ initialSnapshot }} {{ typeof initialSnapshot }} -->
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AutoComplete from 'primevue/autocomplete';
import InputText from 'primevue/inputtext';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type { InvoiceItemSuggestionSource } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import {
  type FormatterType,
  getPrimeVueNativeInput,
  useCleaveController,
} from '@/composables/useCleave';
import { useMultilingualText } from '@/composables/useMultilingualText';
import { translatePrefixedOrValue } from '@/utils/i18n';
import { localeToFlagMap } from '@/utils/multilingualCore';
import { safeStringify } from '@/utils/string';

import PlaceholderSourceButton from './elements/PlaceholderSourceButton.vue';
import FormError from './errors/FormError.vue';

type FormSuggestion = string | Record<string, unknown>;

const i18n = useI18n();

const props = defineProps<{
  name: string;
  modelValue: string | number | undefined;
  initialValue: string | number | undefined;
  placeholder?: string;
  isDirty: (dirty: boolean) => void;
  label?: string;
  type?: 'text' | 'email' | 'number';
  disabled?: boolean;
  suggestions?: FormSuggestion[];
  multiLanguage?: boolean;
  formatType?: FormatterType;
  decimals?: number;
  currency?: string;
  alignRight?: boolean;
  localeOverride?: string;
  formatWhileTyping?: boolean;
  maxlength?: number;
  suggestMinChars?: number;
  suggestCompleteOnFocus?: boolean;
  showEmptyMessage?: boolean;
  formatEmptyAsZero?: boolean;
  acceptPlaceholderOnDblClick?: boolean;
  placeholderSource?: InvoiceItemSuggestionSource;
  onAcceptPlaceholder?: () => void;
  acceptsPlaceholderData?: boolean;
}>();

const suppressDirty = ref(true); // suppress dirty & emits during programmatic changes
const lastChangeByUser = ref(false); // mark user-originated updates

// numeric/currency/percent?
const isNumeric = computed(
  () =>
    props.formatType === 'number' ||
    props.formatType === 'currency' ||
    props.formatType === 'percent' ||
    props.formatType === 'squaremeter',
);

const inputEl = ref<InstanceType<typeof InputText | typeof AutoComplete> | null>(null);

const { t, locale } = useI18n();
// const { errorMessage } = useField<string>(props.name);

const { errorMessage, meta, validate } = useField<string>(props.name, undefined, {
  // do not validate onMount
  validateOnMount: false,
  // don't auto-validate on every value update; we'll trigger validation manually when change came from user
  validateOnValueUpdate: false,
});
const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const viewValue = ref<string>(''); // what is currently displayed in the input
const initialSnapshot = ref<string | number | null>(null); // “frozen” initial value
const hasInitialSnapshot = ref(false);
const lastEmitted = ref<string | number | null>(null);
const internalValue = ref<string | number | null>(canonical(props.modelValue));
const isFieldDirty = ref(false);
const isFocused = ref(false);
const type = ref(props.type || 'text');
const { getTextForLocale, getAvailableLocales } = useMultilingualText();
const isMultilang = computed(() => props.multiLanguage);
const localizedText = computed(() => getTextForLocale(safeStringify(internalValue.value)));
const availableLocales = computed(() => getAvailableLocales(String(internalValue.value)));
const allLocales = Object.keys(localeToFlagMap).filter((l) => l !== 'de');
const missingLocales = computed(() =>
  allLocales.filter((locale) => !availableLocales.value.includes(locale)),
);

// Suggestion list shown by AutoComplete
const acSuggestions = ref<string[]>([]);
const skipNextBlur = ref(false);

// Normalized “source” from props.suggestions
const acSource = computed<string[]>(() => {
  const s = props.suggestions;
  if (!Array.isArray(s)) return [];
  if (typeof s[0] === 'string') {
    return Array.from(new Set(s as string[]));
  }
  // if array of objects: pick the field with the input's name
  return Array.from(
    new Set(
      (s as Record<string, any>[]).map((it) => safeStringify(it[props.name]) ?? '').filter(Boolean),
    ),
  );
});

// Compute placeholder with optional formatting if formatType is set
const formattedPlaceholder = computed(() => {
  // if no placeholder provided → nothing to do
  if (!props.placeholder) return '';

  const translated = translatePrefixedOrValue(i18n, 'placeholder', props.placeholder);

  // if no formatType → try i18n translation with fallback
  if (!props.formatType) return translated;

  // Numeric placeholders should render exactly like values (locale/currency aware).
  const n = toNumber(props.placeholder);
  if (Number.isFinite(n)) return formatForView(n);

  const translatedNumber = toNumber(translated);
  if (Number.isFinite(translatedNumber)) return formatForView(translatedNumber);

  // Not numeric: keep translated/fallback placeholder text.
  return translated;
});

/**
 * Determines if button should be visible
 * Compares current value with placeholder using correct canonical comparison
 */
const isPlaceholderDifferent = computed(() => {
  if (!props.placeholderSource || !props.placeholder || props.disabled) {
    return false;
  }

  const currentCanonical = canonical(internalValue.value);
  const placeholderCanonical = canonical(props.placeholder);

  return !isEqual(currentCanonical, placeholderCanonical);
});

function runWithoutDirty(fn: () => void) {
  const prev = suppressDirty.value;
  suppressDirty.value = true;
  try {
    fn();
  } finally {
    suppressDirty.value = prev;
  }
}

function toNumeric(val: unknown): number | null {
  if (typeof val === 'number') return Number.isFinite(val) ? (Object.is(val, -0) ? 0 : val) : null;
  const s = (safeStringify(val) ?? '').trim();
  if (!s) return props.formatEmptyAsZero ? 0 : null;
  const sNorm = s.replace(/\s+/g, '').replace(',', '.'); // simple de→en decimal
  const n = Number(sNorm);
  return Number.isFinite(n) ? (Object.is(n, -0) ? 0 : n) : null;
}

function isEqual(a: string | number | null, b: string | number | null): boolean {
  return isNumeric.value ? Number(a ?? NaN) === Number(b ?? NaN) : (a ?? '') === (b ?? '');
}

function canonical(val: unknown): string | number | null {
  return isNumeric.value ? toNumeric(val) : (safeStringify(val) ?? '').trim();
}

function onACComplete(e: { query: string }) {
  const q = (e?.query ?? '').toString().trim().toLowerCase();
  const pool = acSource.value;

  // If the caller already provides filtered arrays (e.g., from a store) that's fine – we just do a “soft” filter here.
  acSuggestions.value = q ? pool.filter((v) => v.toLowerCase().includes(q)) : pool; // show everything for empty query (works with completeOnFocus)
}

// Helper: apply the selected/accepted value
function applyValueFromSuggestion(val: unknown) {
  lastChangeByUser.value = true; // user picked a suggestion

  const s = canonical(val);
  internalValue.value = s;
  viewValue.value = String(s); // keep the selected string as the display for now

  skipNextBlur.value = true; // Blur is about to happen (click closes panel) → don't overwrite again
  isFocused.value = false; // focus will usually end due to selection

  // pretty render without causing extra dirty
  runWithoutDirty(() => {
    void nextTick(syncViewFromRaw); // pretty render (e.g., currency) as soon as the panel is closed
  });
}

function onACItemSelect(e: { value: unknown }) {
  applyValueFromSuggestion(e?.value);
}

function addLocaleFlag(locale: string) {
  if (typeof internalValue.value !== 'string') return;
  const flag = localeToFlagMap[locale];
  if (flag && !internalValue.value?.includes(flag)) {
    internalValue.value = (internalValue.value?.trim() ?? '') + ' ' + flag + ' ';
  }
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void;
  (e: 'input', value: string | number | null): void;
}>();

const hasSuggestions = computed(() => Array.isArray(props.suggestions));

const alignClass = computed(() => {
  const wantsRight = props.alignRight ?? isNumeric.value;
  return wantsRight ? 'text-right' : '';
});

function onBlur() {
  if (skipNextBlur.value) {
    skipNextBlur.value = false;
    syncViewFromRaw();
    return;
  }

  isFocused.value = false;

  // --- SPECIAL CASE: AutoComplete + numeric (currency, number, ...) ---
  if (hasSuggestions.value && isNumeric.value) {
    const fromView = parseNumericForAutoComplete(viewValue.value);

    // Treat this as user change so validation behaves as expected
    lastChangeByUser.value = true;
    internalValue.value = fromView;
  } else if (!isNumeric.value) {
    // Text case: simply mirror viewValue into internalValue
    internalValue.value = viewValue.value ?? '';
  }

  // Currency/Number: pretty format with Intl (incl. symbol)
  if (
    props.formatType === 'currency' ||
    props.formatType === 'number' ||
    props.formatType === 'percent' ||
    props.formatType === 'squaremeter'
  ) {
    viewValue.value = formatForView(internalValue.value);
    return;
  }

  syncViewFromRaw();
}

function onFocus() {
  isFocused.value = true;

  if (!cleaveActive.value) {
    void nextTick(() => {
      runWithoutDirty(() => {
        setupCleave();
        if (!applyRawToCleaveAndUpdateView()) {
          viewValue.value = safeStringify(internalValue.value) ?? '';
        }
      });
    });
  } else {
    runWithoutDirty(() => {
      applyRawToCleaveAndUpdateView();
    });
  }

  onACComplete({ query: viewValue.value ?? '' });
}

function onInput(e: any) {
  lastChangeByUser.value = true; // user typed in native input
  if (!isNumeric.value) {
    internalValue.value = (e?.target?.value ?? '').toString(); // text raw
  } else if (!cleaveActive.value) {
    // numeric fallback without cleave
    internalValue.value = canonical(e?.target?.value);
  }
}

const currentLocale = computed(() => props.localeOverride || locale.value);
const decimals = computed(() => props.decimals ?? 2);

// Bridge ref for Cleave: always string in/out
const rawForCleave = computed<string>({
  get() {
    return safeStringify(internalValue.value) ?? '';
  },
  set(s: string) {
    // Cleave writes raw string -> convert back to typed RAW
    // If we're not suppressing, this came from user typing in Cleave
    const typed = isNumeric.value ? toNumeric(s) : (safeStringify(s) ?? '').trim();

    internalValue.value = typed;

    if (!suppressDirty.value) {
      // treat as user input
      lastChangeByUser.value = true;
    }
  },
});

const { cleaveActive, setupCleave, destroyCleave, applyRawToCleaveAndUpdateView } =
  useCleaveController({
    formatterType: computed(() => props.formatType),
    decimals,
    locale: currentLocale,
    isNumeric,
    rawValue: rawForCleave,
    viewValue,
    getInputEl: () => getPrimeVueNativeInput(inputEl.value),
  });

// Helper: formatting for display (unfocused/disabled)
function formatForView(raw: unknown): string {
  if (!isNumeric.value) return safeStringify(raw) ?? '';

  const n = toNumber(raw);
  if (!Number.isFinite(n)) return '';

  const nf = new Intl.NumberFormat(currentLocale.value, {
    style: props.formatType === 'currency' ? 'currency' : 'decimal',
    currency: props.currency,
    minimumFractionDigits: decimals.value,
    maximumFractionDigits: decimals.value,
  });

  const base = nf.format(n);

  // Add suffix depending on formatter type
  if (props.formatType === 'percent') {
    return `${base} %`; // 12,34 %
  }
  if (props.formatType === 'squaremeter') {
    return `${base} m²`; // 12,34 m²
  }

  return base; // number/currency
}

function toNumber(val: unknown): number {
  if (typeof val === 'number') return Number.isFinite(val) ? val : NaN;

  const raw = (safeStringify(val) ?? '').trim();
  if (raw === '') return NaN;

  // Keep only characters that may belong to a number.
  const cleaned = raw.replace(/[^\d,.-]/g, '');
  if (cleaned === '') return NaN;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalized = cleaned;

  // If both separators exist, the right-most one is treated as decimal separator.
  if (lastComma !== -1 && lastDot !== -1) {
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    const groupSeparator = decimalSeparator === ',' ? '.' : ',';
    normalized = normalized.split(groupSeparator).join('');
    if (decimalSeparator === ',') {
      normalized = normalized.replace(',', '.');
    }
  } else if (lastComma !== -1) {
    // Comma-only values are treated as decimal comma.
    normalized = normalized.replace(',', '.');
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
}

// Keep display in sync
function syncViewFromRaw() {
  if (cleaveActive.value && isFocused.value) return;

  runWithoutDirty(() => {
    if (props.disabled && isMultilang.value && !isNumeric.value) {
      viewValue.value = localizedText.value;
      return;
    }

    if (
      props.formatType === 'currency' ||
      props.formatType === 'number' ||
      props.formatType === 'percent' ||
      props.formatType === 'squaremeter'
    ) {
      viewValue.value = formatForView(internalValue.value);
      return;
    }

    if (cleaveActive.value) {
      applyRawToCleaveAndUpdateView();
      return;
    }

    viewValue.value = safeStringify(internalValue.value) ?? '';
  });
}

function onPlaceholderDblClick() {
  if (props.disabled) return;
  if (!props.acceptPlaceholderOnDblClick) return;

  const raw = props.placeholder;
  if (raw == null || raw === '') return;

  // 1) Compute canonical placeholder (raw, unformatted)
  const placeholderCanonical = canonical(raw);

  // 2) Acquire current canonical value
  const currentCanonical = canonical(internalValue.value);

  // 3) Helper: is the field really "empty"?
  // no visible text, only placeholder would be visible
  const isTrulyEmpty =
    (viewValue.value ?? '').trim() === '' || currentCanonical === null || currentCanonical === '';

  // --- CASE A: Toggle OFF (value == placeholder → delete) ---
  if (isEqual(currentCanonical, placeholderCanonical)) {
    lastChangeByUser.value = true; // user-driven change

    // clear internal + viewValue
    if (isNumeric.value && props.formatEmptyAsZero) {
      internalValue.value = 0;
    } else {
      internalValue.value = null;
    }
    viewValue.value = '';

    return;
  }

  // --- CASE B: Toggle ON (only if field is "empty") ---
  if (!isTrulyEmpty) {
    // Something meaningful is already there → don't overwrite
    return;
  }

  lastChangeByUser.value = true;

  // 4) Adopt placeholder as actual value (canonical)
  internalValue.value = placeholderCanonical;

  // 5) Display depending on modus

  if (
    props.formatType === 'currency' ||
    props.formatType === 'number' ||
    props.formatType === 'percent' ||
    props.formatType === 'squaremeter'
  ) {
    // NUMERIC CASE

    // If Cleave is active & focus is in field: Cleave should handle it
    if (cleaveActive.value && isFocused.value) {
      runWithoutDirty(() => {
        applyRawToCleaveAndUpdateView();
      });
    } else {
      // No Cleave input activity → display "nicely" formatted
      viewValue.value = formatForView(placeholderCanonical);
    }
  } else {
    // TEXT CASE
    viewValue.value = safeStringify(placeholderCanonical) ?? '';
  }

  // For AutoComplete: update search list based on new viewValue
  if (hasSuggestions.value && !isNumeric.value && isFocused.value) {
    onACComplete({ query: viewValue.value ?? '' });
  }
}

function handleAcceptPlaceholder() {
  // Icon-click to accept placeholder from specific source (ai / latest / other)
  if (props.disabled || !props.placeholderSource || !props.placeholder) return;

  lastChangeByUser.value = true;

  // 1) Compute canonical placeholder (raw, unformatted)
  const placeholderCanonical = canonical(props.placeholder);

  // 2) Adopt placeholder as actual value (canonical)
  internalValue.value = placeholderCanonical;

  // 3) Display depending on modus
  if (
    props.formatType === 'currency' ||
    props.formatType === 'number' ||
    props.formatType === 'percent' ||
    props.formatType === 'squaremeter'
  ) {
    // NUMERIC CASE
    if (cleaveActive.value && isFocused.value) {
      runWithoutDirty(() => {
        applyRawToCleaveAndUpdateView();
      });
    } else {
      viewValue.value = formatForView(placeholderCanonical);
    }
  } else {
    // TEXT CASE
    viewValue.value = safeStringify(placeholderCanonical) ?? '';
  }

  // For AutoComplete: update search list based on new viewValue
  if (hasSuggestions.value && !isNumeric.value && isFocused.value) {
    onACComplete({ query: viewValue.value ?? '' });
  }

  // Call optional callback to notify parent
  props.onAcceptPlaceholder?.();
}

// Helper: tolerant numeric parsing for AutoComplete (handles "1.234,56", "1.234,56 €", "1234,56")
function parseNumericForAutoComplete(val: unknown): number | null {
  const raw = (safeStringify(val) ?? '').trim();
  if (!raw) return props.formatEmptyAsZero ? 0 : null;

  // keep only digits, comma, dot, minus; strip currency symbols etc.
  const cleaned = raw.replace(/[^\d,.\-]/g, '');

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  let normalized: string;

  if (hasComma && hasDot) {
    // "1.234,56" → "1234.56"
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // "1234,56" → "1234.56"
    // "1234.56" → "1234.56"
    normalized = cleaned.replace(',', '.');
  }

  const n = Number(normalized);
  if (!Number.isFinite(n)) return props.formatEmptyAsZero ? 0 : null;

  return Object.is(n, -0) ? 0 : n;
}

watch(internalValue, (val) => {
  const dirty = !isEqual(internalValue.value, initialSnapshot.value);

  // Emit model updates only when not suppressed (programmatic) OR when user explicitly changed
  if (!suppressDirty.value || lastChangeByUser.value) {
    lastEmitted.value = val ?? '';
    emit('update:modelValue', val);
    emit('input', val);
  }

  // Only mark dirty when not in suppressed section
  const effectiveDirty = !suppressDirty.value && dirty;
  isFieldDirty.value = effectiveDirty;
  props.isDirty(effectiveDirty);

  // Trigger validation only when the change was user-originated
  if (lastChangeByUser.value) {
    void validate();
  }

  // reset user flag after handling
  lastChangeByUser.value = false;
});

watch(
  () => props.initialValue,
  (nv) => {
    runWithoutDirty(() => {
      const next = canonical(nv);
      if (!hasInitialSnapshot.value) {
        initialSnapshot.value = next;
        hasInitialSnapshot.value = true;
      } else if (!isEqual(next, initialSnapshot.value)) {
        if (lastEmitted.value != null && isEqual(next, canonical(lastEmitted.value))) return;
        initialSnapshot.value = next;
      }
      // recompute visual only
      syncViewFromRaw();
      // Update dirty display consistently after snapshot update
      const dirty = !isEqual(internalValue.value, initialSnapshot.value);
      isFieldDirty.value = !suppressDirty.value && dirty;
      props.isDirty(isFieldDirty.value);
    });
  },
  { immediate: true },
);

// Context changes (locale, mode, decimals, currency, disabled) → rebuild display
watch(
  [
    currentLocale,
    () => props.formatType,
    () => props.decimals,
    () => props.currency,
    () => props.disabled,
    isFocused,
  ],
  () => syncViewFromRaw(),
);

watch(viewValue, (val) => {
  if (hasSuggestions.value && isFocused.value) {
    if (isNumeric.value) {
      // normalize numeric string from the input (e.g. "1234,56")
      internalValue.value = canonical(val);
    } else {
      internalValue.value = val ?? '';
    }
  }
});

watch(
  () => props.modelValue,
  (nv) => {
    const next = canonical(nv);
    if (next !== lastEmitted.value) {
      internalValue.value = next;
      syncViewFromRaw();
    }
  },
  { immediate: true },
);

// If the source changes externally (e.g., store updates), update the list
watch(acSource, (arr) => {
  acSuggestions.value = arr;
});

onMounted(() => {
  runWithoutDirty(() => {
    syncViewFromRaw();
    // Cleave initialisieren kann intern schreiben → suppress it
    void nextTick(() => setupCleave());
  });
  // After first tick allow dirty tracking
  void nextTick(() => (suppressDirty.value = false));
});

onBeforeUnmount(() => {
  destroyCleave();
});
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.p-inputtext {
  margin-top: 0;
}
</style>
